import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Search } from 'lucide-react';
import { supabase, JuryMember } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Dialog, { DialogVariant } from '../ui/Dialog';
import AdminSectionSwitch from './AdminSectionSwitch';

interface JuryMemberWithDetails extends JuryMember {
  user_email?: string;
  category_name?: string;
}

interface SimpleCategory {
  id: string;
  name: string;
}

const ManageJury: React.FC = () => {
  const [juryMembers, setJuryMembers] = useState<JuryMemberWithDetails[]>([]);
  const [juryUsers, setJuryUsers] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<SimpleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; title?: string; message: string; variant?: DialogVariant; onConfirm?: () => void; confirmText?: string }>({ open: false, message: '' });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const { isNovagobTeam } = useAuth();

  useEffect(() => {
    if (isNovagobTeam) {
      loadJuryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNovagobTeam]);

  const [searchTerm, setSearchTerm] = useState('');
  const [addMenuFor, setAddMenuFor] = useState<string | null>(null);
  const [addMenuSearch, setAddMenuSearch] = useState('');
  const addMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!addMenuFor) return;
    const onDoc = (e: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
        setAddMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addMenuFor]);

  const loadJuryData = async () => {
    try {
      setLoading(true);
      setMessage('');
      // Cargar categorías disponibles
      const { data: categoriesData, error: categoriesError } = await supabase
        .schema('premios')
        .from('categories')
        .select('id, name')
        .order('name');

      if (categoriesError) throw categoriesError;
      setAllCategories((categoriesData || []) as SimpleCategory[]);

      // Cargar usuarios con rol de jurado desde nuestra vista segura
      const { data: roles, error: rolesError } = await supabase
        .schema('premios')
        .from('roles_with_emails')
        .select('user_id, email')
        .eq('role', 'jury');

      let jury = roles?.map((r: any) => ({
        id: r.user_id,
        email: r.email,
      })) || [];

      if (rolesError || jury.length === 0) {
        // Fallback cuando no haya vista roles_with_emails
        const { data: fallbackRoles, error: fallbackRolesError } = await supabase
          .schema('premios')
          .from('user_roles')
          .select('user_id')
          .eq('role', 'jury');

        if (fallbackRolesError) throw fallbackRolesError;

        const ids = (fallbackRoles || []).map((r: any) => r.user_id).filter(Boolean);
        let emailMap: Record<string, string> = {};

        if (ids.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .schema('id')
            .from('users')
            .select('id, email')
            .in('id', ids);

          if (profilesError) throw profilesError;

          emailMap = (profilesData || []).reduce((acc: Record<string, string>, p: any) => {
            acc[p.id] = p.email || 'Unknown';
            return acc;
          }, {});
        }

        jury = (fallbackRoles || []).map((r: any) => ({
          id: r.user_id,
          email: emailMap[r.user_id] || 'Unknown',
        }));
      }

      setJuryUsers(jury);

      // Cargar asignaciones de jurado a categorías
      const { data: members, error: membersError } = await supabase
        .schema('premios')
        .from('jury_members')
        .select('*');

      if (membersError) throw membersError;

      const memberRows = (members || []) as any[];
      const memberUserIds = Array.from(new Set(memberRows.map((m) => m.user_id).filter(Boolean)));
      const memberCategoryIds = Array.from(new Set(memberRows.map((m) => m.category_id).filter(Boolean)));

      let memberEmailMap: Record<string, string> = {};
      if (memberUserIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .schema('id')
          .from('users')
          .select('id, email')
          .in('id', memberUserIds);

        if (profilesError) throw profilesError;

        memberEmailMap = (profilesData || []).reduce((acc: Record<string, string>, p: any) => {
          acc[p.id] = p.email || 'Unknown';
          return acc;
        }, {});
      }

      let categoryNameMap: Record<string, string> = {};
      if (memberCategoryIds.length > 0) {
        categoryNameMap = ((categoriesData || []) as any[])
          .filter((c) => memberCategoryIds.includes(c.id))
          .reduce((acc: Record<string, string>, c: any) => {
          acc[c.id] = c.name || 'Unknown';
          return acc;
        }, {});
      }

      // Enriquecer con información de usuario y categoría (prioriza email desde profiles)
      const enrichedMembers = memberRows.map((m: any) => {
        const userEmail =
          memberEmailMap[m.user_id] ||
          jury.find((u) => u.id === m.user_id)?.email ||
          'Unknown';
        return {
          ...m,
          user_email: userEmail,
          category_name: categoryNameMap[m.category_id] || 'Unknown',
        };
      });

      setJuryMembers(enrichedMembers);
    } catch (error) {
      console.error('Error loading jury data:', error);
      setMessage('Error cargando jurado/asignaciones. Revisa permisos (RLS) o consola.');
      setJuryUsers([]);
      setJuryMembers([]);
      setAllCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const assignCategoryDirect = async (userId: string, categoryId: string) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .schema('premios')
        .from('jury_members')
        .insert([{ user_id: userId, category_id: categoryId }]);
      if (error) {
        if (error.code === '23505') {
          setMessage('Este jurado ya está en esa categoría.');
        } else {
          throw error;
        }
      } else {
        setMessage('Categoría asignada correctamente.');
        setAddMenuFor(null);
        setAddMenuSearch('');
        loadJuryData();
      }
    } catch (error: any) {
      console.error('Error assigning jury:', error);
      setMessage('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAssignment = (memberId: string) => {
    setDialog({
      open: true,
      title: 'Eliminar asignación',
      message: '¿Estás seguro de que quieres eliminar esta asignación?',
      variant: 'danger',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          setLoading(true);
          const { error } = await supabase
            .schema('premios')
            .from('jury_members')
            .delete()
            .eq('id', memberId);
          if (error) throw error;
          loadJuryData();
        } catch (error: any) {
          console.error('Error removing assignment:', error);
          setDialog({ open: true, title: 'Error', message: 'Error al eliminar la asignación', variant: 'error' });
        } finally {
          setLoading(false);
        }
      },
    });
  };


  const filteredJuryUsers = juryUsers.filter((u) =>
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const assignmentsByUserId = juryMembers.reduce((acc: Record<string, JuryMemberWithDetails[]>, m) => {
    acc[m.user_id] = acc[m.user_id] || [];
    acc[m.user_id].push(m);
    return acc;
  }, {});

  if (!isNovagobTeam) {
    return (
      <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
        <div className="card">
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container dashboard" style={{ padding: '40px 20px' }}>
      <div
        className="dashboard-header"
        style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <h1>Gestión del Jurado</h1>
          <p>Panel de Administración</p>
        </div>
        <AdminSectionSwitch />
      </div>

      {/* Resumen compacto arriba */}
      <div className="grid grid-2" style={{ gap: 16, marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: 22 }}>{juryUsers.length}</div>
          <div className="stat-label">Jurados Totales</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontSize: 22 }}>{juryMembers.length}</div>
          <div className="stat-label">Asignaciones</div>
        </div>
      </div>

      {message && (
        <div
          className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}
          style={{ marginBottom: 16 }}
        >
          {message}
        </div>
      )}

      <div>
        <div className="card">
          <h2 style={{ margin: 0, marginBottom: 6 }}>Asignaciones Actuales</h2>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 13, marginBottom: 16 }}>
            {juryUsers.length} jurado{juryUsers.length === 1 ? '' : 's'} · {juryMembers.length} asignacion{juryMembers.length === 1 ? '' : 'es'}
          </p>

          <div style={{ position: 'relative', marginBottom: 18 }}>
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-secondary)',
              }}
            />
            <input
              type="text"
              placeholder="Buscar por email o categoría..."
              className="form-input"
              style={{ paddingLeft: 34 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredJuryUsers.map((user) => {
              const userAssignments = assignmentsByUserId[user.id] || [];
              const initials = (user.email || '?')
                .split('@')[0]
                .slice(0, 2)
                .toUpperCase();

              return (
                <div
                  key={user.id}
                  style={{
                    padding: 14,
                    borderRadius: 10,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: 'rgba(255, 199, 0, 0.15)',
                        color: 'var(--color-secondary)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.email || 'Unknown'}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                      {userAssignments.length} categoría{userAssignments.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {userAssignments.map((a) => (
                      <span
                        key={a.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 6px 5px 10px',
                          borderRadius: 999,
                          background: 'rgba(255, 199, 0, 0.12)',
                          border: '1px solid rgba(255, 199, 0, 0.35)',
                          color: '#fde68a',
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1,
                        }}
                      >
                        {a.category_name}
                        <button
                          type="button"
                          onClick={() => handleRemoveAssignment(a.id)}
                          disabled={loading}
                          title="Quitar asignación"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.08)',
                            border: 'none',
                            color: '#fde68a',
                            cursor: 'pointer',
                            padding: 0,
                          }}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}

                    {(() => {
                      const missing = allCategories.filter(
                        (c) => !userAssignments.some((a) => a.category_id === c.id)
                      );
                      const canAdd = missing.length > 0;
                      const isOpen = addMenuFor === user.id;
                      const filteredMissing = missing.filter((c) =>
                        c.name.toLowerCase().includes(addMenuSearch.toLowerCase())
                      );
                      return (
                        <div style={{ position: 'relative' }} ref={isOpen ? addMenuRef : null}>
                          <button
                            type="button"
                            onClick={() => {
                              setAddMenuSearch('');
                              setAddMenuFor(isOpen ? null : user.id);
                            }}
                            disabled={loading || !canAdd}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '8px 10px',
                              borderRadius: 999,
                              background: isOpen ? 'rgba(255, 199, 0, 0.12)' : 'transparent',
                              border: `1px dashed ${
                                isOpen ? 'rgba(255, 199, 0, 0.5)' : 'rgba(255,255,255,0.25)'
                              }`,
                              color: canAdd ? 'var(--color-text-secondary)' : 'rgba(255,255,255,0.3)',
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: canAdd ? 'pointer' : 'not-allowed',
                              lineHeight: 1,
                            }}
                          >
                            <Plus size={12} />
                            {!canAdd
                              ? 'Todas asignadas'
                              : userAssignments.length > 0
                              ? 'Añadir'
                              : 'Asignar categoría'}
                          </button>

                          {isOpen && canAdd && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 'calc(100% + 6px)',
                                left: 0,
                                zIndex: 50,
                                minWidth: 260,
                                maxHeight: 280,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                background: '#003554',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 10,
                                boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
                                padding: 8,
                              }}
                            >
                              <div style={{ position: 'relative', marginBottom: 6 }}>
                                <Search
                                  size={13}
                                  style={{
                                    position: 'absolute',
                                    left: 10,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'var(--color-text-secondary)',
                                  }}
                                />
                                <input
                                  autoFocus
                                  type="text"
                                  value={addMenuSearch}
                                  onChange={(e) => setAddMenuSearch(e.target.value)}
                                  placeholder="Buscar categoría..."
                                  className="form-input"
                                  style={{ paddingLeft: 30, fontSize: 13, padding: '6px 8px 6px 30px' }}
                                />
                              </div>
                              <div style={{ overflowY: 'auto', maxHeight: 220 }}>
                                {filteredMissing.length === 0 ? (
                                  <div
                                    style={{
                                      padding: '10px 8px',
                                      fontSize: 12,
                                      color: 'var(--color-text-secondary)',
                                      textAlign: 'center',
                                    }}
                                  >
                                    Sin resultados
                                  </div>
                                ) : (
                                  filteredMissing.map((cat) => (
                                    <button
                                      key={cat.id}
                                      type="button"
                                      disabled={loading}
                                      onClick={() => assignCategoryDirect(user.id, cat.id)}
                                      style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '8px 10px',
                                        borderRadius: 6,
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#fff',
                                        fontSize: 13,
                                        cursor: 'pointer',
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.background =
                                          'rgba(255, 199, 0, 0.12)')
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.background = 'transparent')
                                      }
                                    >
                                      {cat.name}
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>

          {filteredJuryUsers.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
              No se han encontrado jurados con ese criterio.
            </div>
          )}
        </div>

      </div>
      <Dialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmText={dialog.confirmText}
        onConfirm={dialog.onConfirm}
        onClose={closeDialog}
      />
    </div>
  );
};

export default ManageJury;
